import React, { useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { History, ChevronDown, ChevronUp, Calendar } from 'lucide-react';
import { CalendarEvent } from '../../types/calendar';
import { getPastEvents } from '../../utils/eventFeedback';
import { createSafeHtml, decodeHtmlEntities } from '../../utils/security';

interface PastEventsSectionProps {
  events: CalendarEvent[];
  limit?: number;
  hideDescription?: boolean;
}

export default function PastEventsSection({ events, limit = 10, hideDescription = false }: PastEventsSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const pastEvents = getPastEvents(events, limit);

  if (pastEvents.length === 0) {
    return null;
  }

  return (
    <div className="pt-6 border-t border-gray-200">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between text-left group"
        aria-expanded={isExpanded}
        aria-controls="past-events-list"
      >
        <div className="flex items-center space-x-2">
          <History className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900">Past Events</h3>
          <span className="text-sm text-gray-500">({pastEvents.length})</span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-600 group-hover:text-purple-600 transition-colors" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-600 group-hover:text-purple-600 transition-colors" />
        )}
      </button>

      {isExpanded && (
        <div id="past-events-list" className="mt-4 space-y-3">
          {pastEvents.map((event) => {
            const startDate = new Date(event.start);
            const endDate = new Date(event.end);
            const isSameDayEvent = isSameDay(startDate, endDate);

            return (
              <div
                key={`${event.id}-${event.start}`}
                className="bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <Calendar className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {decodeHtmlEntities(event.title)}
                    </h4>
                    <div className="mt-1 text-xs text-gray-500">
                      {event.allDay ? (
                        <span>{format(startDate, 'MMM d, yyyy')}</span>
                      ) : isSameDayEvent ? (
                        <span>
                          {format(startDate, 'MMM d, yyyy • h:mm a')} - {format(endDate, 'h:mm a')}
                        </span>
                      ) : (
                        <span>
                          {format(startDate, 'MMM d, h:mm a')} - {format(endDate, 'MMM d, h:mm a')}
                        </span>
                      )}
                    </div>
                    {!hideDescription && event.description && (
                      <div className="mt-1 text-xs text-gray-600 line-clamp-2">
                        {createSafeHtml(event.description)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {events.filter(e => new Date(e.end) < new Date()).length > limit && (
            <div className="text-center pt-2">
              <p className="text-xs text-gray-500">
                Showing {limit} most recent past events
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
